import * as React from 'react';
import {
  Show,
  SimpleShowLayout,
  useTranslate,
  ArrayField,
  Datagrid,
  TextField,
  BooleanField,
  Labeled,
  useGetOne,
  useShowController,
  Link,
  usePermissions,
  useGetList,
  PrevNextButtons,
  useNotify,
  useDataProvider,
} from 'react-admin';
import {
  Grid,
  Box,
  Typography,
  Stack,
  Chip,
  Theme,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
  Collapse,
  styled,
  IconButtonProps,
  Divider,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Aside from './Aside';
import FullNameField from './FullNameField';
import UserConfigEdit from '../configs/UserConfigEdit';
import UserConfigCreate from '../configs/UserConfigCreate';
import UserConfigView from '../configs/UserConfigView';
import UserSettlementMappings from '../configs/UserSettlementMappings';
import GenerateKYCLink from './GenerateKYCLink';
import RegenerateKYC from './RegenerateKYC';
import ApproveKYC from './ApproveKYC';
import { KYCShowList } from '../kycDocs/KYCShow';
import AddKYCModules from './AddKYCModules';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CardSelectionRequired } from './CardSelectionRequired';
import CustomerCardList from './CustomerCardList';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const UserSettlementTypes = ({ userId }: { userId: string }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = React.useState(false);
  const [itemLoading, setItemLoading] = React.useState<Record<string, boolean>>(
    {}
  );
  const [settlementTypes, setSettlementTypes] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const fetchSettlementTypes = React.useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await dataProvider.customQuery({
        type: 'GET_LIST',
        resource: 'user/settlementTypes',
        payload: { filter: { userId } },
      });

      setSettlementTypes(Array.isArray(result?.data) ? result.data : []);
    } catch (error) {
      setError('Failed to load settlement types');
      notify('Error fetching settlement types', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, userId, notify]);

  React.useEffect(() => {
    fetchSettlementTypes();
  }, [fetchSettlementTypes]);

  const handleToggle = async (id: string, enabled: boolean, item: any) => {
    setItemLoading((prev) => ({ ...prev, [id]: true }));

    try {
      await dataProvider.customQuery({
        type: 'UPDATE',
        resource: 'user/settlementTypes',
        payload: {
          id,
          data: { isActive: !enabled },
          previousData: item,
        },
      });

      fetchSettlementTypes();
      notify('Settlement type updated successfully', { type: 'success' });
    } catch (error) {
      notify('Error updating settlement type', { type: 'error' });
    } finally {
      setItemLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  // Helper to safely extract field values
  const getValue = (item: any, keys: string[], defaultValue: any = '') => {
    for (const key of keys) {
      if (item[key] !== undefined) return item[key];
    }
    return defaultValue;
  };

  // Group settlement types by payment type
  const groupedByPaymentType = React.useMemo(() => {
    const groups: Record<string, any[]> = {};

    settlementTypes.forEach((type) => {
      const paymentType = getValue(type, ['paymentTypeDesc', 'paymentType']);
      if (!groups[paymentType]) {
        groups[paymentType] = [];
      }
      groups[paymentType].push(type);
    });

    // Sort payment types alphabetically
    return Object.keys(groups)
      .sort()
      .map((paymentType) => ({
        paymentType,
        items: groups[paymentType].sort((a, b) => {
          // Sort by settlement type name/description
          const aDesc = getValue(a, ['description', 'settlementType', 'name']);
          const bDesc = getValue(b, ['description', 'settlementType', 'name']);
          return aDesc.localeCompare(bDesc);
        }),
      }));
  }, [settlementTypes]);

  return (
    <>
      <Typography variant='h6' gutterBottom>
        User Settlement Options
      </Typography>

      {loading ? (
        <Typography>Loading settlement types...</Typography>
      ) : error ? (
        <Box
          sx={{
            p: 2,
            color: 'error.main',
            bgcolor: 'error.light',
            borderRadius: 1,
          }}
        >
          {error}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Payment Type</TableCell>
                <TableCell>Settlement Type</TableCell>
                <TableCell align='center'>Enabled</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedByPaymentType.length > 0 ? (
                groupedByPaymentType.map((group) => (
                  <React.Fragment key={`payment-type-${group.paymentType}`}>
                    {group.items.map((type, index) => {
                      const id = getValue(type, ['id', '_id']);
                      const isEnabled = getValue(
                        type,
                        ['enabled', 'isActive'],
                        false
                      );

                      return (
                        <TableRow
                          key={id || `row-${Math.random()}`}
                          sx={
                            index === 0
                              ? {
                                  '& td:first-of-type': {
                                    borderTop:
                                      '2px solid rgba(224, 224, 224, 1)',
                                    fontWeight: 'bold',
                                  },
                                }
                              : {}
                          }
                        >
                          <TableCell>
                            {index === 0 ? group.paymentType : ''}
                          </TableCell>
                          <TableCell>
                            {getValue(type, [
                              'description',
                              'settlementType',
                              'name',
                            ])}
                          </TableCell>
                          <TableCell align='center'>
                            <Switch
                              checked={!!isEnabled}
                              onChange={() =>
                                id && handleToggle(id, !!isEnabled, type)
                              }
                              disabled={!id || itemLoading[id]}
                              size='small'
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align='center'>
                    No settlement types found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

const CustomerShow = (props: any) => {
  const { permissions } = usePermissions();
  const { record } = useShowController();
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { data, error } = useGetOne(
    'userConfigs',
    { id: record?.userId },
    {
      enabled: !!record?.userId,
    }
  );

  const { data: kycList = [] } = useGetList('kyc', {
    pagination: { page: 1, perPage: 10 },
    sort: { field: 'createdAt', order: 'DESC' },
    filter: { transactionId: record?.kycTransactionId, q: undefined },
  });

  const aadhaarSelfieMatch = kycList.find(
    (kyc: any) =>
      kyc.kycModuleTypeId === 'Aadhaar & Selfie Match' && kyc.validated
  );

  const aadhaarPanMatch = kycList.find(
    (kyc: any) => kyc.kycModuleTypeId === 'Aadhaar & Pan Match' && kyc.validated
  );

  const selfie = kycList.find(
    (kyc: any) => kyc.kycModuleTypeId === 'Selfie Capture'
  );

  const aadhaar =
    kycList.find(
      (kyc: any) =>
        kyc.kycModuleTypeId === 'Aadhaar Digi Locker' && kyc.validated
    ) ||
    kycList.find(
      (kyc: any) => kyc.kycModuleTypeId === 'Aadhaar XML' && kyc.validated
    );

  const pan = kycList.find(
    (kyc: any) => kyc.kycModuleTypeId === 'PAN' && kyc.validated
  );

  const allModulesCompleted =
    selfie && aadhaar && pan && aadhaarPanMatch && aadhaarSelfieMatch;

  console.log('data?.settlementTypeList:', data?.settlementTypeList);

  const [expanded, setExpanded] = React.useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <Show
        component={'div'}
        title={<CustomerTitle />}
        {...(!isSmall && {
          aside: (
            <Box width={400}>
              <Aside />
            </Box>
          ),
        })}
      >
        <SimpleShowLayout sx={{ padding: 0 }}>
          <PrevNextButtons
            linkType='show'
            sort={{ field: 'lastSeenAt', order: 'DESC' }}
          />
          <Card>
            <CardContent>
              <Grid container width={{ xs: '100%', xl: 800 }} spacing={2}>
                <Grid item md={12} minWidth='100%'>
                  <Typography variant='h6' gutterBottom>
                    Personal Details
                  </Typography>
                  <Grid container>
                    <Grid item xs={12} sm={12} md={6}>
                      <Labeled source='userId' label='User ID'>
                        <TextField source='userId' />
                      </Labeled>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Labeled label='Name'>
                        <Stack direction='row' spacing={0.5}>
                          <TextField source='personalDetails.firstName' />
                          <TextField source='personalDetails.lastName' />
                        </Stack>
                      </Labeled>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} mt={1}>
                      <Labeled
                        label={<OtpLabel param={'sms'} record={record} />}
                      >
                        <TextField source='personalDetails.mobile' />
                      </Labeled>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} mt={1}>
                      <Labeled
                        label={<OtpLabel param={'email'} record={record} />}
                      >
                        <TextField source='personalDetails.email' />
                      </Labeled>
                    </Grid>
                  </Grid>
                  <CardSelectionRequired record={record} />
                  <Box mt='1em' />

                  <Typography variant='h6' gutterBottom>
                    KYC Details
                  </Typography>
                  <Grid container>
                    <Grid item xs={12} sm={12} md={6}>
                      <Labeled label='KYC Transaction ID'>
                        <Stack direction='row' alignItems='center' spacing={2}>
                          <TextField source='kycTransactionId' />
                          <RegenerateKYC customerId={record?.userId} />
                        </Stack>
                      </Labeled>
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}>
                      <Labeled source='kycStatus' label='KYC Status'>
                        <TextField source='kycStatus' />
                      </Labeled>
                    </Grid>
                  </Grid>
                  <>
                    {kycList.length > 0 && (
                      <Stack flex={1} mt={2} mb={2}>
                        <KYCShowList transactionId={record?.kycTransactionId} />
                      </Stack>
                    )}
                    {kycList.length > 0 && (
                      <AddKYCModules
                        kycTransactionId={record?.kycTransactionId}
                        aadhaar={aadhaar}
                        pan={pan}
                        selfie={selfie}
                        aadhaarPan={aadhaarPanMatch}
                        aadhaarSelfie={aadhaarSelfieMatch}
                      />
                    )}
                    {allModulesCompleted && (
                      <ApproveKYC
                        customerId={record?.userId}
                        kycTransactionId={record?.kycTransactionId}
                      />
                    )}
                    {(record?.kycStatus === 'not_started' ||
                      record?.kycStatus === 'user_cancelled') && (
                      <GenerateKYCLink customerId={record?.userId} />
                    )}
                  </>
                  {/* <ArrayField source="kycDocument">
              <Datagrid bulkActionButtons={false}>
                <TextField source="docType" label="Doc Type" />
                <TextField source="customerName" label="Name" />
                <TextField source="docNumber" label="Number" />
                <BooleanField source="kycValidated" label="Validated" />
              </Datagrid>
            </ArrayField> */}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </SimpleShowLayout>
        <Box mt='1em' />

        <Card sx={{ width: '100%', mb: 2 }}>
          <CardContent>
            <Typography variant='h6'>Customer Cards</Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', xl: '900px' },
                mx: 'auto',
                px: { xs: 0, sm: 1, md: 2 },
                mt: 2,
              }}
            >
              <CustomerCardList userId={record?.userId} expanded={true} />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {!permissions?.includes('user_config_edit') &&
              permissions?.includes('user_config_view') && <UserConfigView />}

            {permissions?.includes('user_config_edit') &&
              (data?.id ? (
                <UserConfigEdit userId={record?.userId} />
              ) : (
                <UserConfigCreate userId={record?.userId} />
              ))}

            <Divider sx={{ my: 3 }} />

            {record?.userId && (
              <UserSettlementMappings userId={record?.userId} />
            )}

            <Divider sx={{ my: 3 }} />

            {record?.userId && <UserSettlementTypes userId={record?.userId} />}
          </CardContent>
        </Card>
        {isSmall && (
          <Box px={1}>
            <Aside />
          </Box>
        )}
      </Show>
    </>
  );
};

const CustomerTitle = () => (
  <FullNameField size='32' sx={{ margin: '5px 0' }} />
);

const OtpLabel = ({
  param,
  record,
}: {
  param: 'sms' | 'email';
  record: any;
}) => {
  const keys = {
    sms: {
      label: 'Mobile',
      validated: 'smsOtpValidated',
      otp: 'smsOtp',
    },
    email: {
      label: 'Email',
      validated: 'emailOtpValidated',
      otp: 'emailOtp',
    },
  };

  if (!record.otpInfo)
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {keys[param]?.label}
      </span>
    );

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {keys[param]?.label}
      {record.otpInfo[keys[param].validated] ? (
        <CheckCircleIcon color='primary' sx={{ height: '16px' }} />
      ) : (
        <Chip
          label={
            <>
              {' '}
              OTP <b> {record.otpInfo[keys[param].otp]}</b>
            </>
          }
          variant='outlined'
          style={{
            padding: '2px 0px',
            fontSize: '14px',
            height: '22px',
            background: '#00000011',
            marginLeft: '10px',
          }}
        ></Chip>
      )}
    </span>
  );
};

export default CustomerShow;
